import React, { useEffect, useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, IdCell, Input, Modal, PageHead,
  SearchBox, Switch, Textarea, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { brandApi, companyApi } from '../api';
import { useToast } from '../context/ToastContext';

const emptyForm = {
  name: '', tagline: '', description: '', logo: '', banner: '',
  manufacturer: '', isFeatured: false, isActive: true, sortOrder: 0,
};

const Brands = () => {
  const toast = useToast();
  const list = useList(brandApi.list, { search: '', isActive: '' });

  const [manufacturers, setManufacturers] = useState([]);
  const [form, setForm] = useState(null);
  const [remove, setRemove] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    companyApi
      .list({ type: 'manufacturer', verificationStatus: 'approved', limit: 100 })
      .then(res => setManufacturers(res.data))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Give the brand a name');
    if (!form.manufacturer) return toast.error('Pick the manufacturer that owns this brand');

    setBusy(true);
    try {
      if (form._id) await brandApi.update(form._id, form);
      else await brandApi.create(form);
      toast.success(form._id ? 'Brand updated' : 'Brand created');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRemove = async () => {
    setBusy(true);
    try {
      await brandApi.remove(remove._id);
      toast.success('Brand deleted');
      setRemove(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'name', label: 'Brand', render: row => <IdCell image={row.logo} name={row.name} sub={row.tagline || `/${row.slug}`} /> },
    {
      key: 'manufacturer',
      label: 'Owned by',
      render: row => (
        <>
          <div>{row.manufacturer?.name || '—'}</div>
          <div className="cell-muted mono">{row.manufacturer?.code}</div>
        </>
      ),
    },
    { key: 'description', label: 'About', render: row => <span className="text-sm text-muted">{(row.description || '—').slice(0, 70)}</span> },
    {
      key: 'flags',
      label: 'Status',
      render: row => (
        <div className="row" style={{ gap: 5 }}>
          {row.isFeatured && <Badge status="featured" tone="brand" dot={false}>Featured</Badge>}
          <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Live' : 'Hidden'}</Badge>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row, manufacturer: row.manufacturer?._id || '' })}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => setRemove(row)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Brands"
        subtitle="Tarpaulin brands registered by manufacturers on the network."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New brand</Button>}
      />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search brands…" />
        <select className="select" style={{ width: 160 }} value={list.filters.isActive} onChange={e => list.setFilter('isActive', e.target.value)}>
          <option value="">Any status</option>
          <option value="true">Live</option>
          <option value="false">Hidden</option>
        </select>
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} brands` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit brand' : 'New brand'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Create brand'}</Button>
          </>
        }
      >
        {form && (
          <>
            <Input label="Brand name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Tarpex Shield" />
            <div className="field">
              <label>Manufacturer</label>
              <select className="select" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })}>
                <option value="">Select a verified manufacturer…</option>
                {manufacturers.map(m => (
                  <option key={m._id} value={m._id}>{m.name} ({m.code})</option>
                ))}
              </select>
              <div className="hint">Only verified manufacturers can own brands.</div>
            </div>
            <Input label="Tagline" value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder="Built to withstand every season" />
            <Textarea label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            <div className="grid-2">
              <Input label="Logo URL" value={form.logo} onChange={e => setForm({ ...form, logo: e.target.value })} />
              <Input label="Banner URL" value={form.banner} onChange={e => setForm({ ...form, banner: e.target.value })} />
            </div>
            <div className="col" style={{ gap: 11 }}>
              <Switch checked={form.isFeatured} onChange={v => setForm({ ...form, isFeatured: v })} label="Feature this brand in the app" />
              <Switch checked={form.isActive} onChange={v => setForm({ ...form, isActive: v })} label="Visible to buyers" />
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!remove}
        danger
        title="Delete this brand?"
        message={`"${remove?.name}" will be removed. Blocked while products still reference it.`}
        confirmLabel="Delete"
        loading={busy}
        onConfirm={doRemove}
        onClose={() => setRemove(null)}
      />
    </>
  );
};

export default Brands;
